import { PLAY_BOARD_NAME, PLAY_BOARD_PATH } from '@/features/play/copy'
import { isShortIdShape } from '@/lib/seo/shortId'
import type { NavIconKey } from './navIcons'
import { HEADER_NAV_LINKS } from './publicNav'

/**
 * WHERE THE MENUS CAN GO — every nav destination in one plain module, no JSX.
 *
 * Split out of AppNavLinks (which draws rows) so that the LIST is stated once
 * and read by everything: the workspace sidebar, the in-app drawer, the club
 * storefront's drawer, the public sheet and the avatar dropdown. Keeping it free
 * of JSX also means a test can import it and assert on the real thing rather
 * than grep a component for a label — the list is the part that changes, so it
 * is the part worth holding still.
 */

export type NavItem = {
  href: string
  label: string
  icon: NavIconKey
  isActive?: (pathname: string) => boolean
}

// Detail-page predicate: pathname must be /<base>/<shortId>(/<slug>)?
// Anything else under /<base>/ — /new, /edit, etc. — is NOT a
// browsing route and should leave the nav item dim.
function browsingActive(pathname: string, base: string): boolean {
  if (pathname === base) return true
  const prefix = `${base}/`
  if (!pathname.startsWith(prefix)) return false
  const segments = pathname.slice(prefix.length).split('/').filter(Boolean)
  const head = segments[0] ?? ''
  return isShortIdShape(head)
}

/**
 * `/play` — the door in front of the match board, and the row's ONE href.
 *
 * The board itself is `/play/games`, behind RequireAuth. `/play` is the
 * auth-aware answer in front of it (app/router/PlayHubRedirect): signed in → the
 * board, signed out → `/clubs`, `checking` → the auth ring. That rule is written
 * there and must not be written twice, which is what an auth-aware href in this
 * menu would be — the same rule in a second place, drifting from the first.
 */
const PLAY_ENTRY_PATH = '/play'

/**
 * THE MATCH BOARD ROW — one row, one name, one destination.
 *
 * NAMED FROM ONE PLACE, NOT NAMED HERE. The row reads whatever `PLAY_BOARD_NAME`
 * says — "Find a game" today (owner, 2026-09-07), "Official matches" the day
 * before, "Play" before that. Three names in two days is the argument for the
 * import: the label and the board's route are stated once in features/play/copy.ts,
 * where the page's own H1 also reads them, so a rename can never leave the menu and
 * the page saying different things again. The constant is called PLAY_BOARD_ITEM
 * after the PAGE, not after a label that keeps moving.
 *
 * IT POINTS AT THE DOOR, NOT AT THE BOARD, and for one viewer only that is not
 * where they land. The menu draws for signed-out visitors too (the public sheet on
 * /clubs, /players, /tournaments, /ladders), and `/play/games` would hand them a
 * bare login wall — the regression `/play`'s redirect exists to prevent. So the
 * href is the door for everybody, and a signed-in viewer pays one invisible hop:
 * a client-side `<Navigate replace>` through a route element the router imports
 * eagerly, so it costs no request and leaves no history entry.
 *
 * WHAT THE HOP COSTS IS PAID BACK HERE. `isActive` lights the row anywhere under
 * `/play` — the door and the board it opens — so the sidebar highlight follows the
 * viewer to `/play/games` instead of dimming the moment the redirect lands.
 */
export const PLAY_BOARD_ITEM: NavItem = {
  href: PLAY_ENTRY_PATH,
  label: PLAY_BOARD_NAME,
  icon: 'play',
  isActive: (pathname) =>
    pathname === PLAY_ENTRY_PATH ||
    pathname === PLAY_BOARD_PATH ||
    pathname.startsWith(`${PLAY_BOARD_PATH}/`),
}

/**
 * THE MENU — every surface's leading group, and there is only one of it.
 *
 * ONE LIST, NOT TWO (owner, 2026-09-07: «все еще есть кнопка Home в меню»). The
 * workspace list dropped Home on 2026-09-06 as he asked; the public list kept it,
 * so the same hamburger opened six rows on /clubs and five inside the desk, and the
 * board row pointed at two different places. Two lists differing by one row and one
 * href is the two-vocabulary disease this weekend's commits have been curing, in its
 * last hiding place — so the lists are now one array, held by one name, and there is
 * no second list for a row to creep back into.
 *
 * HOME IS GONE, AND NOTHING REPLACES IT HERE. The way out to the landing page is the
 * wordmark in the header and in the drawer's own head, plus the footer's "powered by
 * TheRacketCenter.com" — not a menu row that reads like a section of the product.
 *
 * EVERY CATALOGUE ROW SAYS "ALL" (owner, 2026-09-07). Standing on a club's
 * storefront, "Tournaments" reads as THIS club's tournaments — and tapping it
 * opened the platform-wide list, so the visitor left the club without being told.
 * The owner refused the heavy cures (club-scoped routes, a club context that follows
 * you) and named the honest one: the club page's own tabs are the club's activities,
 * and this menu is the platform view, so it should SAY the platform view. Nothing
 * routes differently; the surprise goes because the label stops implying "the club
 * you are in". On /clubs the row "All clubs" is still true.
 *
 * Guides is deliberately absent — it is outward reference content, owned by the
 * marketing header (and the mobile drawer's COMPANY group). See
 * docs/ux/2026-07-18-nav-top-plus-sidebar.md §2.
 */
export const APP_NAV_ITEMS: NavItem[] = [
  PLAY_BOARD_ITEM,
  { href: '/clubs', label: 'All clubs', icon: 'clubs' },
  {
    href: '/players',
    label: 'All players',
    icon: 'players',
    isActive: (pathname) =>
      pathname === '/players/all' || browsingActive(pathname, '/players'),
  },
  {
    href: '/tournaments',
    label: 'All tournaments',
    icon: 'tournaments',
    isActive: (pathname) => browsingActive(pathname, '/tournaments'),
  },
  { href: '/ladders', label: 'All ladders', icon: 'ladders' },
]

// Guides — outward reference content, reused in the account + signed-out tails.
export const GUIDES_ITEM: NavItem = { href: '/guides', label: 'Guides', icon: 'guides' }

// COMPANY — the marketing / company links (Product / Pricing / Solutions /
// Guides / About us) the horizontal top-bar header carries. That header nav is
// hidden below 1024px, so the mobile drawer (DrawerNavContent) surfaces the same
// set there. Routes + labels stay sourced from HEADER_NAV_LINKS (publicNav) so
// the header and drawer can't drift; we only pair an existing line-icon per
// route here to render them in the shared grouped idiom.
const COMPANY_ICON_BY_ROUTE: Record<string, NavIconKey> = {
  '/for-clubs': 'product',
  '/pricing': 'pricing',
  '/solutions': 'workspace',
  '/guides': 'guides',
  '/about': 'profile',
}
export const COMPANY_ITEMS: NavItem[] = HEADER_NAV_LINKS.map((link) => ({
  href: link.to,
  label: link.label,
  icon: COMPANY_ICON_BY_ROUTE[link.to] ?? 'guides',
}))

// MANAGE + ACCOUNT rows as shared NavItems, so the sidebar, the mobile drawer,
// and the desktop avatar dropdown all draw Workspace / Admin / Profile from one
// source (Workspace is gated by isAnyClubStaff, Admin by isSuperAdmin at render).
export const WORKSPACE_ITEM: NavItem = { href: '/workspace', label: 'Workspace', icon: 'workspace' }
export const ADMIN_ITEM: NavItem = { href: '/admin', label: 'Admin', icon: 'admin' }
export const MANAGE_ITEMS: NavItem[] = [WORKSPACE_ITEM, ADMIN_ITEM]
export const PROFILE_ITEM: NavItem = { href: '/profile', label: 'Profile', icon: 'profile' }
/** Help — the account menu and the drawer's Account group, between Profile and Sign
 *  out, from this one item so the two cannot drift (docs/ux/2026-09-24-help-center.md §6). */
export const HELP_ITEM: NavItem = { href: '/help', label: 'Help', icon: 'help' }

/** The MANAGE rows a viewer may see: Workspace for club staff, Admin for a
 *  super-admin. One gate for the drawer and the avatar dropdown, so a surface
 *  cannot quietly offer (or withhold) a manage link the other one doesn't. */
export function manageItemsFor(viewer: { clubStaff: boolean; superAdmin: boolean }): NavItem[] {
  return [
    ...(viewer.clubStaff ? [WORKSPACE_ITEM] : []),
    ...(viewer.superAdmin ? [ADMIN_ITEM] : []),
  ]
}
